import { Step } from 'react-joyride';

/**
 * Tour identifiers for the Data Explorer application
 */
export const DATA_EXPLORER_TOURS = {
    FIRST_QUERY: 'first-query-tour',
    ADVANCED_FEATURES: 'advanced-features-tour',
    NEW_CLIENT_WORKFLOW: 'new-client-workflow-tour',
} as const;

export type TourId = typeof DATA_EXPLORER_TOURS[keyof typeof DATA_EXPLORER_TOURS];

/**
 * Tour step definitions for all guided tours in the Data Explorer
 */
export const TOUR_STEPS: Record<TourId, Step[]> = {
    /**
     * First Query Tour - Primary onboarding experience
     * Guides new users through their first complete query workflow
     */
    [DATA_EXPLORER_TOURS.FIRST_QUERY]: [
        {
            target: '[data-tour="country-selector"]',
            content: 'Choose Your Market: Start by selecting your market (Denmark, Norway, or Sweden). This will determine which data sources and clients are available to you.',
            title: 'Step 1: Choose Your Market 🎯',
            placement: 'bottom',
            disableBeacon: true,
            hideFooter: false,
            styles: {
                options: {
                    width: 320,
                },
            },
        },
        {
            target: '[data-tour="table-selection"]',
            content: 'Select your data source from the available tables. Different sources provide different types of advertising and performance data.',
            title: 'Step 2: Choose Data Source',
            placement: 'right',
            hideFooter: false,
        },
        {
            target: '[data-tour="client-search"]',
            content: 'Search and select the clients you want to analyze. You can search by client name or agency. Multiple clients can be selected for comparative analysis.',
            title: 'Step 3: Select Clients',
            placement: 'top',
            hideFooter: false,
        },
        {
            target: '[data-tour="date-selector"]',
            content: 'Set your date range to analyze data from specific time periods. You can select preset ranges (like "Last 30 days") or choose custom dates.',
            title: 'Step 4: Choose Date Range',
            placement: 'bottom',
            hideFooter: false,
        },
        {
            target: '[data-tour="column-search"]',
            content: 'Use this search to quickly find columns when working with large datasets. Type to filter the available columns by name.',
            title: 'Step 5: Filter Columns',
            placement: 'bottom',
            hideFooter: false,
        },
        {
            target: '[data-tour="dimension-box"]',
            content: 'Drag columns here to group your data. Dimensions help you slice and dice your data by different categories like date, country, or campaign.',
            title: 'Step 6: Add Dimensions',
            placement: 'bottom',
            hideFooter: false,
        },
        {
            target: '[data-tour="metrics-box"]',
            content: 'Drag metrics here to define what you want to measure. Metrics are the numerical values you want to analyze, like impressions, clicks, or spend.',
            title: 'Step 7: Add Metrics',
            placement: 'bottom',
            hideFooter: false,
        },
        {
            target: '[data-tour="filter-box"]',
            content: 'Add filters to refine your analysis. Filters help you focus on specific subsets of data, like particular date ranges or campaign types. This is optional but very powerful!',
            title: 'Step 8: Add Filters (Optional)',
            placement: 'bottom',
            hideFooter: false,
        },
        {
            target: '[data-tour="preview-button"]',
            content: 'Click preview to execute your query and see the results! You can always modify your dimensions, metrics, filters, and dates, then preview again.',
            title: 'Step 9: Preview Your Data 🚀',
            placement: 'top',
            hideFooter: false,
        },
        {
            target: '[data-tour="download-button"]',
            content: 'Once satisfied with your preview results, click Download to export the complete dataset as a CSV file. Perfect for further analysis in Excel or other tools!',
            title: 'Step 10: Download Complete Dataset 📁',
            placement: 'top',
            hideFooter: false,
        },
    ],

    /**
     * Advanced Features Tour - For users who want to discover more capabilities
     */
    [DATA_EXPLORER_TOURS.ADVANCED_FEATURES]: [
        {
            target: '[data-tour="column-search"]',
            content: 'Use the search box to quickly find columns when working with large datasets. The search works across column names and descriptions.',
            title: 'Search Columns',
            placement: 'bottom',
            disableBeacon: true,
            hideFooter: false,
        },
        {
            target: '[data-tour="dimension-box"] [data-tour="drag-handle"]',
            content: 'Drag dimensions, metrics, and filters to reorder them. The order affects how your data is grouped and displayed in results.',
            title: 'Reorder Items',
            placement: 'right',
            hideFooter: false,
        },
        {
            target: '[data-tour="filter-box"]',
            content: 'Add filters to refine your analysis. Filters help you focus on specific subsets of data, like particular date ranges or campaign types.',
            title: 'Filter Your Data',
            placement: 'bottom',
            hideFooter: false,
        },
        {
            target: '[data-tour="date-selector"]',
            content: 'Adjust your date range to analyze data from specific time periods. You can select preset ranges or choose custom dates.',
            title: 'Customize Date Range',
            placement: 'bottom',
            hideFooter: false,
        },
    ],

    /**
     * New Client Workflow Tour - Focused on the streamlined client selection process
     */
    [DATA_EXPLORER_TOURS.NEW_CLIENT_WORKFLOW]: [
        {
            target: '[data-tour="country-selector"]',
            content: "We've simplified the process! Now you only need to select your country - client filtering handles the agencies automatically.",
            title: 'Streamlined Workflow',
            placement: 'bottom',
            disableBeacon: true,
            hideFooter: false,
        },
        {
            target: '[data-tour="client-search"]',
            content: 'Search by client name OR agency name. The system automatically handles agency associations - no separate agency selection needed!',
            title: 'Smart Client Search',
            placement: 'right',
            hideFooter: false,
        },
    ],
};

/**
 * Helper function to get steps for a specific tour
 */
export const getTourSteps = (tourId: TourId): Step[] => {
    return TOUR_STEPS[tourId] || [];
};

/**
 * Helper function to check if a tour ID is valid
 */
export const isValidTourId = (tourId: string): tourId is TourId => {
    return Object.values(DATA_EXPLORER_TOURS).includes(tourId as TourId);
};
